import React, { useState } from 'react';
import { auth } from '../firebase';
import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { signInWithGoogleCalendar } from '../lib/googleAuth';
import {
  getSignInMethodsForEmail,
  isGoogleOnlySignIn,
  linkEmailPasswordToUser,
  emailsMatchForLinking,
} from '../lib/authAccountLinking';
import AccountLinkingPanel from './auth/AccountLinkingPanel';
import { TextField, Button, Container, Typography, Box, Alert, Link, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AppBrandLogo from './AppBrandLogo';
import LoadingLockScreen from './LoadingLockScreen';

const SignUp = ({ onToggle }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [linkFlow, setLinkFlow] = useState(null);

  const navigate = useNavigate();

  const cancelLinkFlow = () => {
    setLinkFlow(null);
    setError(null);
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    cancelLinkFlow();

    try {
      await createUserWithEmailAndPassword(auth, email, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Error signing up:', err);
      if (err.code === 'auth/email-already-in-use') {
        const methods = await getSignInMethodsForEmail(email);
        if (isGoogleOnlySignIn(methods)) {
          setLinkFlow({ email: email.trim(), password });
          setError(null);
          return;
        }
        setError('An account with this email already exists. Try signing in instead.');
      } else if (err.code === 'auth/account-exists-with-different-credential') {
        setLinkFlow({ email: email.trim(), password });
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else {
        setError(err.message || 'Sign-up failed.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleLinkVerify = async () => {
    if (!linkFlow) return;
    setError(null);
    setSubmitting(true);
    try {
      const userCredential = await signInWithGoogleCalendar();
      if (!emailsMatchForLinking(userCredential.user.email, linkFlow.email)) {
        await signOut(auth);
        setError(`Please sign in with Google using ${linkFlow.email}.`);
        return;
      }
      await linkEmailPasswordToUser(
        userCredential.user,
        linkFlow.email,
        linkFlow.password,
      );
      setLinkFlow(null);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign-up Google link:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in cancelled. Try again when ready.');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak. Use at least 6 characters.');
      } else {
        setError(err.message || 'Could not link your password.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="xs">
      <LoadingLockScreen open={submitting} />
      <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <AppBrandLogo maxWidth={300} sx={{ mb: 2 }} />
        <Typography component="h2" variant="h5">
          Sign Up
        </Typography>

        {linkFlow ? (
          <AccountLinkingPanel
            variant="google-first"
            email={linkFlow.email}
            onGoogleVerify={handleGoogleLinkVerify}
            onCancel={cancelLinkFlow}
            submitting={submitting}
            error={error}
          />
        ) : (
          <Box component="form" onSubmit={handleSignUp} sx={{ mt: 1, width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email-signup"
              label="Email Address"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password-signup"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={submitting}
            >
              Sign Up
            </Button>

            {error && <Alert severity="error">{error}</Alert>}

            <Stack direction="row" justifyContent="flex-end">
              <Link href="#" variant="body2" onClick={onToggle}>
                Already have an account? Login
              </Link>
            </Stack>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default SignUp;
